import React, { useRef, useState, useEffect, useCallback, useMemo, useImperativeHandle } from "react";
import { Modal, Button } from "react-bootstrap";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { t } from "../../i18n";
import './PhotoUpload.css';
import useApp from "../../AppContext";

export default React.forwardRef(function PhotoUpload(props, ref) {
     const app = useApp();
     const cameraInputRef = useRef(null);
     const libraryInputRef = useRef(null);
     const [showOptions, setShowOptions] = useState(false);
     const [selectedImages, setSelectedImages] = useState([]); 
     const serverUrl = props.serverUrl || '';
     const userToken = props.userToken || '';
     const lastNotifiedRef = useRef(null);
     const removedServerPathsRef = useRef([]); 

    const serverBase = useMemo(() => {
        try {
            if (!serverUrl) return '';
            return serverUrl.replace(/\/+$/, '');
        } catch {
            return serverUrl;
        }
    }, [serverUrl]);

    const serverPathToUrl = useCallback((p) => {
        if (!p) return null;
        const trim = String(p).trim();
        if (trim.startsWith('blob:')) return trim;
        if (/^https?:\/\//.test(trim)) return trim;
        if (trim.startsWith('/')) {
            try {
                return new URL(trim.replace(/\/{2,}/g, '/'), serverBase || window.location.origin).toString();
            } catch {
                return `${serverBase}${trim}`.replace(/\/{2,}/g, '/');
            }
        }
        try {
            return new URL(`/${trim}`.replace(/\/{2,}/g, '/'), serverBase || window.location.origin).toString();
        } catch {
            return `${serverBase}/${trim}`.replace(/\/{2,}/g, '/');
        }
    }, [serverBase]);

    useEffect(() => {
        setSelectedImages([]);
        if (!props.photoUrl) {
            lastNotifiedRef.current = null;
            return;
        }
        const parts = String(props.photoUrl).split(',').map(x => x.trim()).filter(Boolean);
        const imgs = parts.map(p => {
            const safePath = p.startsWith('/') ? p : (p ? `/${p}` : null);
            return {
                file: null,
                url: serverPathToUrl(safePath),
                uploading: false,
                serverPath: safePath
            };
        });
        setSelectedImages(imgs);
        lastNotifiedRef.current = parts.join(', ');
    }, [props.photoUrl, serverPathToUrl]);

    const onInputClick = (e) => { e.target.value = null; };

    const uploadFileToServer = async (file) => {
        if (!serverUrl) return null;
        try {
            const fd = new FormData();
            fd.append('file', file);
            const headers = {};
            if (userToken) headers['Authorization'] = userToken;
            const res = await fetch(`${serverUrl}/api/image/upload`, {
                method: 'POST',
                body: fd,
                headers
            });
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(`Upload failed: ${res.status} ${txt}`);
            }
            let json = null;
            try { json = await res.json(); } catch { }
            if (json && (json.path || json.filePath)) return json.path || json.filePath;
            if (json && json.filename) return `/uploadedImages/${json.filename}`;
            return `/uploadedImages/${file.name}`;
        } catch (err) {
            console.error("PhotoUpload - upload error:", err);
            return null;
        }
    };

    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setShowOptions(false);

        setSelectedImages(prev => {
            const copy = [...prev];
            for (const file of files) {
                const objectUrl = URL.createObjectURL(file);
                copy.push({ file, url: objectUrl, uploading: false, serverPath: null });
            }
            return copy;
        });
     };

    const deleteFromServer = async (serverPath) => {
        if (!serverPath || !serverUrl) return false;
        try {
            const parts = serverPath.split('/').filter(Boolean);
            const fileName = parts.pop();
            if (!fileName) return false;
            const headers = {};
            if (userToken) headers['Authorization'] = userToken;
            const res = await fetch(`${serverUrl}/api/image/delete?filename=${encodeURIComponent(fileName)}`, {
                method: 'DELETE',
                headers
            });
            return res.ok;
        } catch (err) {
            console.error('deleteFromServer error', err);
            return false;
        }
    };
    const handleRemoveImage = (index) => {
        const img = selectedImages[index];
        if (!img) return;
        if (img.url && img.url.startsWith('blob:')) {
            try { URL.revokeObjectURL(img.url); } catch (e) {}
        }
        if (img.serverPath) {
            removedServerPathsRef.current = Array.from(new Set([...removedServerPathsRef.current, img.serverPath]));
        }
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
    };

    useImperativeHandle(ref, () => ({
        applyPendingChanges: async () => {
            const imgs = Array.from(selectedImages);
            for (let i = 0; i < imgs.length; i++) {
                const it = imgs[i];
                if (it.file && !it.serverPath) {
                    try {
                        const uploadedPath = await uploadFileToServer(it.file);
                        const normalized = uploadedPath ? (uploadedPath.startsWith('/') ? uploadedPath : `/${uploadedPath}`) : null;
                        const fullUrl = normalized ? serverPathToUrl(normalized) + `?_ts=${Date.now()}` : it.url;
                        imgs[i] = { file: null, url: fullUrl, uploading: false, serverPath: normalized };
                    } catch (err) {
                        console.error('upload pending file failed', err);
                        imgs[i] = { ...it, uploading: false };
                    }
                }
            }
            setSelectedImages(imgs);
            const toDelete = Array.from(new Set(removedServerPathsRef.current || []));
            for (const path of toDelete) {
                try {
                    await deleteFromServer(path);
                } catch (err) {
                    console.warn('Failed to delete image on server', path, err);
                }
            }
            removedServerPathsRef.current = [];

            const remaining = imgs.map(i => i.serverPath).filter(Boolean);
            const finalString = remaining.length > 0 ? remaining.join(', ') : null;
            if (typeof props.onImagesChange === 'function') {
                setTimeout(() => {
                    try { props.onImagesChange(finalString); lastNotifiedRef.current = finalString; }
                    catch (err) { console.error('onImagesChange error', err); }
                }, 0);
            }
            return finalString;
        }
    }));

    return (
        <>
            <div className="text-center text-muted add-photo-container" style={{ marginBottom: "75px", minHeight: 110 }}>
                <div className="d-block cursor-pointer p-4" onClick={() => setShowOptions(true)}>
                    {selectedImages.length === 0 ? (
                        <div style={{ color: '#D9D9D9' }}>
                            <AddPhotoAlternateIcon style={{ fontSize: '50px' }} />
                            <p className="mt-2">{t("photoUpload.addPhotos", props.language)}</p>
                        </div>
                    ) : (
                        <div style={{ color: "var(--color-main-background)" }}>
                            <i className="bi bi-plus-circle me-2"></i> {t("photoUpload.addMorePhotos", props.language)}
                        </div>
                    )}
                </div>

                <input type="file" accept="image/*" capture="environment" ref={cameraInputRef}
                    onChange={handleImageChange} onClick={onInputClick} style={{ display: 'none' }} multiple />
                <input type="file" accept="image/*" ref={libraryInputRef}
                    onChange={handleImageChange} onClick={onInputClick} style={{ display: 'none' }} multiple />

                <div className="d-flex flex-wrap justify-content-center mt-3" data-photo-count={selectedImages.length}>
                    {selectedImages.map((img, index) => (
                        <div key={index} className="position-relative m-2" style={{ width: '80px', height: '80px' }}>
                            <img
                                src={img.url}
                                alt={`preview-${index}`}
                                onError={(e) => { e.currentTarget.style.opacity = 0.4; console.warn('image load error', img.url); }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '1px solid #ccc',
                                    filter: img.uploading ? 'grayscale(40%)' : 'none',
                                }}
                            />
                            <DeleteIcon onClick={() => handleRemoveImage(index)} className="position-absolute top-0 end-0 m-1 trash-can-photo" style={{ cursor: 'pointer' }} />
                        </div>
                    ))}
                </div>
            </div>

            <Modal
                show={showOptions}
                onHide={() => setShowOptions(false)}
                centered
                dialogClassName={`modal-bottom-sheet ${showOptions ? 'show' : ''} overflow-hidden`}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body className="text-center">
                    <Button 
                        variant="light" 
                        className="w-100 mb-2" 
                        onClick={() => cameraInputRef.current?.click()}
                        style={{
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-secondary)',
                            borderColor: 'var(--border-color)'
                        }}
                    >
                        <CameraAltIcon className='me-2' /> {t("photoUpload.openCamera", props.language)}
                    </Button>
                    <Button 
                        variant="light" 
                        className="w-100" 
                        onClick={() => libraryInputRef.current?.click()}
                        style={{
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-secondary)',
                            borderColor: 'var(--border-color)'
                        }}
                    >
                        <PhotoLibraryIcon className='me-2' /> {t("photoUpload.chooseFromLibrary", props.language)}
                    </Button>
                    <Button 
                        variant="secondary" 
                        className="w-100 mt-3" 
                        onClick={() => setShowOptions(false)}
                        style={{
                            backgroundColor: app.theme === 'dark' ? '#495057' : '#6c757d',
                            borderColor: app.theme === 'dark' ? '#495057' : '#6c757d'
                        }}
                    >
                        {t("common.cancel", props.language)}
                    </Button>
                </Modal.Body>
            </Modal>
        </>
    );
});
